import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { generatePath, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { InstantSearch } from 'react-instantsearch';
import { Business_Profile_Enum } from '@kheops/graphql';
import { useSearchClient } from '../hooks/useSearchClient';
import { SearchContract } from '../search/product/SearchProductHit';
import SearchProductHits from '../search/product/SearchProductHits';
import { companyPageDisplayModeAtom } from '../search/state/state';
import { currentContextAtom } from '../state';
import { EmptyPagePlaceholderProps } from '../common/components/EmptyPagePlaceholder';
import TrumpeterButterflyImage from '../assets/images/trumpeter_butterfly.png';
import { RoutePaths } from '../routes/AppRoutes';
import SearchPagination from '../search/SearchPagination';
import FilterUtils from '../search/filters/filter.utils';
import { getSearchFamiliesFromCurrentFamilies } from '../discovery/discovery.utils';
import useUpdateHistoryUiState from '../discovery/hooks/useUpdateHistoryUiState';
import CompanyProductFilters from './CompanyProductFilters';
import CompanyProductConfigure from './CompanyProductConfigure';

interface CompanyProductSearchProps {
  companyId: string;
  contextCompanyId: string;
  contract?: SearchContract;
  searchInputRef?: React.RefObject<HTMLInputElement | null>;
}

export default function CompanyProductSearch({ companyId, contextCompanyId, contract, searchInputRef }: CompanyProductSearchProps): React.JSX.Element {
  const { t } = useTranslation(['products']);
  const { realm } = useAtomValue(currentContextAtom);
  const searchClient = useSearchClient();
  const location = useLocation();
  const displayMode = useAtomValue(companyPageDisplayModeAtom);
  const theme = useTheme();
  const isDownXl = useMediaQuery(theme.breakpoints.down('xl'));
  const productFilters = useMemo(() => {
    return `company_id:${companyId}${contract ? ` AND ${FilterUtils.visiblePackagingsForBuyerFilter(contract.buyingCompanyId)}` : ''}`;
  }, []);
  const index = import.meta.env.REACT_APP_ALGOLIA_PACKAGING_INDEX;
  const { updateUiState } = useUpdateHistoryUiState({ index });

  const emptyPagePlaceholderProps = useMemo((): EmptyPagePlaceholderProps => {
    let props: EmptyPagePlaceholderProps;

    if (realm === Business_Profile_Enum.Buyer) {
      props = {
        title: t('products:no_product'),
        description: t('products:no_product_available_for_this_supplier'),
        imageSrc: TrumpeterButterflyImage,
      };
    } else {
      props = {
        title: t('products:no_product'),
        description: t('products:no_product_available_description'),
        imageSrc: TrumpeterButterflyImage,
        buttonLabel: t('products:see_my_products'),
        linkDestination: generatePath(RoutePaths.COMPANY_PRODUCTS),
      };
    }

    return props;
  }, []);

  const initialUiState = useMemo(() => {
    if (!window.history.state?.uiState?.[index]?.refinementList?.family && location.state?.productFamily) {
      const families = getSearchFamiliesFromCurrentFamilies(location.state.productFamily, location.state.productSubFamily || 'ALL');
      window.history.replaceState({
        ...window.history.state,
        uiState: {
          ...window.history.state?.uiState,
          [index]: {
            ...window.history.state?.uiState?.[index],
            refinementList: {
              ...window.history.state?.uiState?.[index]?.refinementList,
              family: families,
            },
          },
        },
      }, '');
    }
    const state = window.history.state?.uiState?.[index];

    if (!state) {
      return undefined;
    }

    return {
      [index]: state,
    };
  }, []);

  return (
    <InstantSearch
      indexName={index}
      searchClient={searchClient}
      initialUiState={initialUiState}
      onStateChange={updateUiState}
    >
      <CompanyProductConfigure
        isDownXl={isDownXl}
        filters={productFilters}
        contract={contract}
      />
      <CompanyProductFilters
        contextCompanyId={contextCompanyId}
        searchInputRef={searchInputRef}
      />
      <Box sx={{ mt: 1 }}>
        <SearchProductHits
          hitProps={{ hideCompany: true }}
          displayMode={displayMode}
          cardSizes={{ md: 4 }}
          currentContract={contract}
          companyId={companyId}
          emptyPlaceholderProps={emptyPagePlaceholderProps}
        />
        <SearchPagination sx={{ width: 'fit-content', mt: 2 }} />
      </Box>
    </InstantSearch>
  );
}
